import {
  TextInput,
  Grid,
  Stack,
  ScrollArea,
  Button,
  LoadingOverlay,
  Select,
  Table,
  ActionIcon,
  Group,
  Modal,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import {
  CREATE_PRODUCT_SKUS,
  DEL_PRODUCT_SKU,
  UPDATE_PRODUCT_SKUS,
} from "apollo/mutuations";
import { FiPlus, FiTrash, FiEdit } from "react-icons/fi";
import { customLoader } from "components/utilities/loader";
import { showNotification } from "@mantine/notifications";
import { GET_PRODUCT_SKUS } from "apollo/queries";
const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const AddPriceModal = ({ id, setOpened, activePage, productData }) => {
  const form = useForm({
    initialValues: {
      variants: [],
      sell_price: "",
      buy_price: "",
    },
  });

  const [attributes, setAttributeValues] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [loading, setLoading] = useState(false);
  const { height } = useViewportSize();
  const [productSku, setProductSku] = useState([]);
  const [editingSku, setEditingSku] = useState(null);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [skuToDelete, setSkuToDelete] = useState(null);

  const [addValue, { loading: addValues }] = useMutation(CREATE_PRODUCT_SKUS);
  const [deleteValue] = useMutation(DEL_PRODUCT_SKU);
  const [updateValue, { loading: updating }] = useMutation(UPDATE_PRODUCT_SKUS);

  const { loading: productSkuLoading, refetch } = useQuery(GET_PRODUCT_SKUS, {
    variables: { product_id: productData?.id },
    onCompleted(data) {
      console.log(data);
      setProductSku(data.productSkus || []);
    },
    onError(error) {
      console.error("Error fetching product SKUs:", error);
    },
  });

  useEffect(() => {
    if (productData) {
      setAttributeValues(productData?.attributes);
    }
  }, [productData]);
  useEffect(() => {
    refetch();
  }, []);

  const handleAttributeChange = (attributeId, selectedValueId) => {
    const updated = {
      ...selectedAttributes,
      [attributeId]: selectedValueId,
    };
    setSelectedAttributes(updated);
  };

  const handleAddValue = () => {
    const attribute_value_ids =
      Object.values(selectedAttributes).filter(Boolean);
    const { sell_price, buy_price } = form.values;

    if (attribute_value_ids.length === 0 || !sell_price || !buy_price) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          "Please select at least one attribute and enter both Buy and Sell prices.",
      });
      return;
    }

    const isDuplicateInForm = form.values.variants.some(
      (variant) =>
        variant.attribute_value_ids.length === attribute_value_ids.length &&
        variant.attribute_value_ids.every((val) =>
          attribute_value_ids.includes(val),
        ),
    );

    const isDuplicateInExisting = productSku.some(
      (sku) =>
        sku.variants.length === attribute_value_ids.length &&
        sku.variants.every((v) => attribute_value_ids.includes(v.id)),
    );

    if (isDuplicateInForm || isDuplicateInExisting) {
      showNotification({
        color: "red",
        title: "Error",
        message: "This combination already exists!",
      });
      return;
    }

    const newVariant = {
      attribute_value_ids,
      sell_price: parseFloat(sell_price),
      buy_price: parseFloat(buy_price),
      isNew: true,
    };

    form.setFieldValue("variants", [newVariant, ...form.values.variants]);
    form.setValues({
      sell_price: "",
      buy_price: "",
    });
    setSelectedAttributes({});
    setEditingSku(null);
  };

  const handleEdit = (sku) => {
    const variantMap = {};
    sku.variants.forEach((v) => {
      variantMap[v.attribute.id] = v.id;
    });
    setSelectedAttributes(variantMap);
    form.setValues({
      sell_price: sku.sell_price.toString(),
      buy_price: sku.buy_price.toString(),
    });
    setProductSku(productSku.filter((item) => item.id !== sku.id));
    setEditingSku(sku);
  };

  const handleUpdate = () => {
    if (!editingSku) return;

    const attribute_value_ids =
      Object.values(selectedAttributes).filter(Boolean);
    const { sell_price, buy_price } = form.values;

    if (attribute_value_ids.length === 0 || !sell_price || !buy_price) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          "Please select at least one attribute and enter both Buy and Sell prices.",
      });
      return;
    }

    setLoading(true);
    updateValue({
      variables: {
        id: editingSku.id,
        attribute_value_ids,
        sell_price: parseFloat(sell_price),
        buy_price: parseFloat(buy_price),
      },
      onCompleted(data) {
        const updatedSku = data.updateProductSku;
        if (updatedSku) {
          setProductSku((prevSkus) => [
            {
              ...editingSku,
              buy_price: updatedSku.buy_price,
              sell_price: updatedSku.sell_price,
              variants: updatedSku.variants || editingSku.variants,
            },
            ...prevSkus,
          ]);
          showNotification({
            color: "green",
            title: "Success",
            message: "Price Updated Successfully",
          });
        } else {
          showNotification({
            color: "red",
            title: "Error",
            message: "No updated SKU data returned from mutation",
          });
        }
        setEditingSku(null);
        form.setValues({
          sell_price: "",
          buy_price: "",
        });
        setSelectedAttributes({});
        setLoading(false);
      },
      onError(error) {
        setLoading(false);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to update price",
        });
      },
    });
  };

  const handleRemove = (item, isNew, index, skuId) => {
    if (isNew) {
      const updated = [...form.values.variants];
      updated.splice(index, 1);
      form.setFieldValue("variants", updated);
      showNotification({
        color: "green",
        title: "Success",
        message: "Variant removed successfully",
      });
    } else {
      setSkuToDelete(skuId);
      setOpenedDelete(true);
    }
  };

  const deletingSku = () => {
    if (!skuToDelete) return;

    setLoading(true);
    deleteValue({
      variables: { id: skuToDelete },
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "SKU deleted successfully",
        });
        setProductSku(productSku.filter((sku) => sku.id !== skuToDelete));
        setLoading(false);
        setOpenedDelete(false);
        setSkuToDelete(null);
      },
      onError(error) {
        setLoading(false);
        showNotification({
          color: "red",
          title: "Error",
          message: error?.graphQLErrors?.[0]?.message || "Failed to delete SKU",
        });
        setOpenedDelete(false);
        setSkuToDelete(null);
      },
    });
  };

  const submit = () => {
    setLoading(true);
    if (form.validate().hasErrors) {
      setLoading(false);
      return;
    }

    const newVariants = form.values.variants.filter((v) => !v.originalId);

    if (newVariants.length === 0) {
      showNotification({
        color: "red",
        title: "Error",
        message: "Please add at least one new variant before submitting.",
      });
      setLoading(false);
      return;
    }

    let createVariables = {
      product_id: productData.id,
      variants: newVariants.map((variant) => ({
        attribute_value_ids: variant.attribute_value_ids,
        sell_price: variant.sell_price,
        buy_price: variant.buy_price,
      })),
    };
    addValue({
      variables: createVariables,
      onCompleted(data) {
        const newSkus = data.createProductSkus || [];
        setProductSku((prevSkus) => [...newSkus, ...prevSkus]);
        showNotification({
          color: "green",
          title: "Success",
          message: "Price Created Successfully",
        });
        setOpened(false);
        setLoading(false);
        form.setFieldValue("variants", []);
      },
      onError(error) {
        setLoading(false);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to create price",
        });
      },
    });
  };

  const allAttributeNames = Array.from(
    new Set([
      ...(attributes?.map((attr) => attr.name) || []),
      ...(productSku?.flatMap((sku) =>
        sku.variants.map((variant) => variant.attribute.name),
      ) || []),
    ]),
  );

  return (
    <>
      <LoadingOverlay
        visible={addValues || loading || updating || productSkuLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <Modal
        opened={openedDelete}
        onClose={() => {
          setOpenedDelete(false);
          setSkuToDelete(null);
        }}
        title="Warning"
        centered
      >
        <p>Are you sure you want to delete this SKU?</p>
        <Group position="right">
          <Button onClick={() => deletingSku()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>

      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Stack>
            <Grid>
              <Grid.Col span={12}>
                {attributes.length > 0 && (
                  <Grid>
                    {attributes.map((attribute) => (
                      <Grid.Col span={3} key={attribute.id}>
                        <Select
                          label={attribute.name}
                          placeholder={`Select ${attribute.name}`}
                          data={attribute.values.map((val) => ({
                            value: val.id,
                            label: val.value,
                          }))}
                          value={selectedAttributes[attribute.id] || ""}
                          onChange={(val) =>
                            handleAttributeChange(attribute.id, val)
                          }
                          searchable
                          clearable
                        />
                      </Grid.Col>
                    ))}
                  </Grid>
                )}
              </Grid.Col>

              <Grid.Col span={3}>
                <TextInput
                  placeholder="Sell Price"
                  label="Sell Price"
                  value={form.values.sell_price}
                  type="number"
                  min={0}
                  step="any"
                  {...form.getInputProps("starting_price", {
                    type: "input",
                  })}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      e.key === "Minus" ||
                      e.key === "NumpadSubtract"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput
                  type="number"
                  placeholder="Buy Price"
                  label="Buy Price"
                  min="0"
                  value={form.values.buy_price}
                  onChange={(e) =>
                    form.setFieldValue("buy_price", e.target.value)
                  }
                />
              </Grid.Col>
              <Grid.Col span={3} mt={22}>
                <Button
                  style={{
                    width: editingSku ? "30%" : "20%",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  fullWidth
                  onClick={editingSku ? handleUpdate : handleAddValue}
                  loading={updating}
                >
                  {editingSku ? "Update" : "Add"}
                </Button>
              </Grid.Col>
              <Grid.Col span={12}>
                <Table
                  withBorder
                  withColumnBorders
                  striped
                  highlightOnHover
                  mt="md"
                >
                  <thead>
                    <tr>
                      {allAttributeNames.map((attrName) => (
                        <th
                          style={{ textTransform: "capitalize" }}
                          key={attrName}
                        >
                          {capitalizeFirstLetter(attrName)}
                        </th>
                      ))}
                      <th>Buy Price</th>
                      <th>Sell Price</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.values.variants.map((variant, idx) => (
                      <tr key={`variant-${idx}`}>
                        {allAttributeNames.map((attrName) => {
                          const attr = attributes.find(
                            (a) => a.name === attrName,
                          );
                          const matched = attr?.values.find((v) =>
                            variant.attribute_value_ids.includes(v.id),
                          );
                          return (
                            <td key={attrName}>
                              {capitalizeFirstLetter(matched?.value)}
                            </td>
                          );
                        })}
                        <td>{variant.buy_price}</td>
                        <td>{variant.sell_price}</td>
                        <td>
                          <Group spacing="xs">
                            <ActionIcon
                              color="red"
                              onClick={() =>
                                handleRemove(variant, true, idx, null)
                              }
                            >
                              <FiTrash />
                            </ActionIcon>
                          </Group>
                        </td>
                      </tr>
                    ))}
                    {productSku.map((sku) => {
                      const variantMap = {};
                      sku.variants.forEach((v) => {
                        variantMap[v.attribute.name] = v.value;
                      });
                      return (
                        <tr key={`sku-${sku.id}`} style={{ cursor: "pointer" }}>
                          {allAttributeNames.map((attrName) => (
                            <td key={attrName}>
                              {capitalizeFirstLetter(variantMap[attrName])}
                            </td>
                          ))}
                          <td>{sku.buy_price}</td>
                          <td>{sku.sell_price}</td>
                          <td>
                            <Group spacing="xs">
                              <ActionIcon
                                color="red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemove(sku, false, null, sku.id);
                                }}
                                title="Delete"
                              >
                                <FiTrash />
                              </ActionIcon>
                              <ActionIcon
                                color="blue"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(sku);
                                }}
                                title="Edit"
                              >
                                <FiEdit />
                              </ActionIcon>
                            </Group>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Grid.Col>
              <Grid.Col span={12}>
                <Button
                  style={{
                    width: "20%",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  fullWidth
                  type="submit"
                  disabled={loading}
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};

export default AddPriceModal;
